import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { completeWarrantyRegistrationFormSchema } from '@ekhodealer/ekho-common';
import { Button, Checkbox, Group, SkeletonRect,Stack, Text, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useCompleteWarrantyRegistration } from '../../../api/progress/useCompleteWarrantyRegistration';
import { useGetCompleteWarrantyRegistration } from '../../../api/progress/useGetCompleteWarrantyRegistration';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface CompleteWarrantyRegistrationDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * CompleteWarrantyRegistrationDetail component for completing warranty registration.
 * Displays a checkbox for confirmation and a submit button.
 */
export const CompleteWarrantyRegistrationDetail = ({
	actionItem,
}: CompleteWarrantyRegistrationDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const completeWarrantyRegistrationMutation = useCompleteWarrantyRegistration(orderId);
	const { data: existingData, isLoading: isLoadingData } =
		useGetCompleteWarrantyRegistration(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(completeWarrantyRegistrationFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.warrantyRegistrationComplete) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await completeWarrantyRegistrationMutation.mutateAsync();
			toast({
				message: 'Warranty registration completed successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to complete warranty registration';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm that the warranty registration has been completed.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.warrantyRegistrationComplete ? (
					<Text size='body' color='success'>
						✓ Warranty registration already complete
					</Text>
				) : null}

				{/* Show form if not complete */}
				{!existingData?.warrantyRegistrationComplete && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group itemsSpacing='8' itemsAlignY='center' itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting ||
												completeWarrantyRegistrationMutation.isPending
											}
											ariaLabel='Confirm warranty registration complete'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || completeWarrantyRegistrationMutation.isPending}
							isDisabled={isLoadingData}>
							{actionItem.config.buttonTitle || 'Complete warranty registration'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
